import apiClient from './client';
import {
  Camera,
  Church,
  FileText,
  Film,
  Gem,
  Mail,
  Map as MapIcon,
  Music,
  Newspaper,
  Paperclip,
  ScrollText,
  type LucideIcon,
} from 'lucide-react';

// ─── Typen ──────────────────────────────────────────────────────────────────

export type MediaCategory =
  | 'photo'
  | 'document'
  | 'certificate'
  | 'letter'
  | 'artifact'
  | 'map'
  | 'newspaper'
  | 'churchbook'
  | 'audio'
  | 'video'
  | 'other';

export const MEDIA_CATEGORIES: MediaCategory[] = [
  'photo',
  'document',
  'certificate',
  'letter',
  'artifact',
  'map',
  'newspaper',
  'churchbook',
  'audio',
  'video',
  'other',
];

export const MEDIA_CATEGORY_LABELS: Record<MediaCategory, string> = {
  photo: 'Foto',
  document: 'Dokument',
  certificate: 'Urkunde',
  letter: 'Brief',
  artifact: 'Erbstück',
  map: 'Karte',
  newspaper: 'Zeitung',
  churchbook: 'Kirchenbuch',
  audio: 'Audio',
  video: 'Video',
  other: 'Sonstiges',
};

// Icons statt Emojis: Emojis sehen auf jedem System anders aus und passen
// nicht zur übrigen Oberfläche, die durchgehend Lucide verwendet.
export const MEDIA_CATEGORY_ICONS: Record<MediaCategory, LucideIcon> = {
  photo: Camera,
  document: FileText,
  certificate: ScrollText,
  letter: Mail,
  artifact: Gem,
  map: MapIcon,
  newspaper: Newspaper,
  churchbook: Church,
  audio: Music,
  video: Film,
  other: Paperclip,
};

/**
 * Wer an welcher Stelle auf einem Foto zu sehen ist. x/y relativ zur
 * Bildgröße (0..1, von oben links). Das Bild selbst bleibt unverändert.
 */
export interface Markierung {
  id: string;
  x: number;
  y: number;
  personId?: string | null;
  /** Freier Name – oder der zuletzt bekannte Name der verknüpften Person. */
  name?: string | null;
  notiz?: string | null;
}

export interface ArchiveMedia {
  id: string;
  personId: string | null;
  albumId: string | null;
  filename: string;
  originalName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  title: string | null;
  description: string | null;
  dateTaken: string | null;
  category: MediaCategory | null;
  tags: string[];
  sourceRef: string | null;
  sourceUrl: string | null;
  location: string | null;
  /** Markierungen auf dem Bild (nur bei Bildern). */
  markierungen?: Markierung[];
  createdAt: string;
  /** Primärperson (denormalisiert). */
  person?: {
    id: string;
    firstName: string;
    lastName: string | null;
  } | null;
  /** Alle zugeordneten Personen (Mehrfachzuordnung). */
  persons: {
    id: string;
    firstName: string;
    lastName: string | null;
  }[];
  album?: {
    id: string;
    name: string;
  } | null;
}

export interface ArchiveListResponse {
  data: ArchiveMedia[];
  total: number;
  page: number;
  limit: number;
}

export interface ArchiveFilterParams {
  q?: string;
  category?: MediaCategory;
  personId?: string;
  albumId?: string; // UUID oder 'none' für Medien ohne Album
  tag?: string;
  unassigned?: boolean;
  page?: number;
  limit?: number;
}

// ─── Listing ───────────────────────────────────────────────────────────────

export async function fetchArchive(
  params: ArchiveFilterParams = {},
): Promise<ArchiveListResponse> {
  const query: Record<string, string> = {};
  if (params.q) query.q = params.q;
  if (params.category) query.category = params.category;
  if (params.personId) query.personId = params.personId;
  if (params.albumId) query.albumId = params.albumId;
  if (params.tag) query.tag = params.tag;
  if (params.unassigned) query.unassigned = 'true';
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const { data } = await apiClient.get<ArchiveListResponse>('/archive', { params: query });
  return data;
}

// ─── Stats ─────────────────────────────────────────────────────────────────

export interface ArchiveStats {
  total: number;
  unassigned: number;
  byCategory: { category: string; count: number }[];
  topTags: { tag: string; count: number }[];
}

export async function fetchArchiveStats(): Promise<ArchiveStats> {
  const { data } = await apiClient.get<ArchiveStats>('/archive/stats');
  return data;
}

// ─── Upload ────────────────────────────────────────────────────────────────

export interface ArchiveUploadInput {
  files: File[];
  personId?: string;
  personIds?: string[];
  albumId?: string;
  category?: MediaCategory;
  tags?: string[];
  title?: string;
  description?: string;
  sourceRef?: string;
  sourceUrl?: string;
  location?: string;
  dateTaken?: string;
}

export async function uploadArchiveFiles(input: ArchiveUploadInput): Promise<ArchiveMedia[]> {
  const formData = new FormData();
  for (const file of input.files) formData.append('files', file);
  if (input.personId) formData.append('personId', input.personId);
  if (input.personIds?.length) formData.append('personIds', JSON.stringify(input.personIds));
  if (input.albumId) formData.append('albumId', input.albumId);
  if (input.category) formData.append('category', input.category);
  if (input.tags?.length) formData.append('tags', JSON.stringify(input.tags));
  if (input.title) formData.append('title', input.title);
  if (input.description) formData.append('description', input.description);
  if (input.sourceRef) formData.append('sourceRef', input.sourceRef);
  if (input.sourceUrl) formData.append('sourceUrl', input.sourceUrl);
  if (input.location) formData.append('location', input.location);
  if (input.dateTaken) formData.append('dateTaken', input.dateTaken);

  const { data } = await apiClient.post<ArchiveMedia[]>('/archive/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ─── Update / Delete / Detail ──────────────────────────────────────────────

export interface UpdateArchiveInput {
  title?: string | null;
  description?: string | null;
  date_taken?: string | null;
  category?: MediaCategory | null;
  tags?: string[];
  sourceRef?: string | null;
  sourceUrl?: string | null;
  location?: string | null;
  personId?: string | null;
  personIds?: string[];
  albumId?: string | null;
}

export interface BatchUpdateInput {
  ids: string[];
  category?: MediaCategory | null;
  personId?: string | null;
  albumId?: string | null;
  date_taken?: string | null;
  tagsAdd?: string[];
  tagsSet?: string[];
  tagsRemove?: string[];
}

export async function batchUpdateArchive(
  input: BatchUpdateInput,
): Promise<{ affected: number }> {
  const { data } = await apiClient.patch<{ affected: number }>('/archive/batch', input);
  return data;
}

export async function updateArchiveItem(
  id: string,
  input: UpdateArchiveInput,
): Promise<ArchiveMedia> {
  const { data } = await apiClient.patch<ArchiveMedia>(`/archive/${id}`, input);
  return data;
}

export async function fetchArchiveItem(id: string): Promise<ArchiveMedia> {
  const { data } = await apiClient.get<ArchiveMedia>(`/archive/${id}`);
  return data;
}

export async function deleteArchiveItem(id: string): Promise<void> {
  await apiClient.delete(`/archive/${id}`);
}

// ─── Markierungen ──────────────────────────────────────────────────────────

export async function saveMarkierungen(mediaId: string, markierungen: Markierung[]): Promise<ArchiveMedia> {
  const { data } = await apiClient.put<ArchiveMedia>(`/archive/${mediaId}/markierungen`, { markierungen });
  return data;
}
